import { Colors, ConfirmationModal, Pagination, ProgressDisplay, ToastVariant, useProgress } from "@factorearth/component-library";
import { FormGroup, FormTemplate, FormTemplateForResolver, Project, SyncConfig, UserForResolver } from "db/definitions";
import useClickOutside from "hooks/useClickOutside";
import { Location } from "hooks/useDrawer";
import { useAnalytics } from "hooks/useAnalytics";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import paginate from "utils/paginate";
import { sortByAlphaNumericalArray, sortByDate } from "utils/sort";
import CreateFormGroupFolder from "../CreateFormGroupFolder/CreateFormGroupFolder";
import styles from "./FormGroupList.module.scss";
import FormGroupListItem from "./FormGroupListItem/FormGroupListItem";
import DevFormGroupOptions from "./FormGroupListOptions/DevOptions/DevFormGroupOptions";
import FormGroupListOptions from "./FormGroupListOptions/FormGroupListOptions";
import { syncSelectedTemplatesOffline } from "utils/templateSyncModules";
import { db } from "db/db";
import { useErrorBoundary } from "react-error-boundary";
import useAutoSync from "hooks/useAutoSync";
import useProjectConfig from "hooks/useProjectConfig";
import type NewFormGroupData from "components/FormGroupList/FormGroupList";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import { ChangePreview } from "services/gisUpload/types";
import { previewGeoCSVImport, applyGeoCSVImportFromPreview } from "services/gisUpload/geoCsvUpload";
import { uploadData } from "aws-amplify/storage";
import { downloadFormGroupJson } from "db/merge";
import { recordSentry } from "utils/recordSentry";

const dropdownData = [
	{ id: 0, name: "Sort By" },
	{ id: 1, name: "Filter By" },
	{ id: 2, name: "Options" },
];
interface ToastContent {
	title: string;
	message: string;
	type: ToastVariant;
}

interface NewFormGroupData {
	name: string;
	templates: string[];
	type: "default" | "photolog";
}

interface Props {
	addFormGroupsInState?: (formGroups: FormGroup[]) => void;
	closeCreateNew?: () => void;
	color: Colors;
	createNewToggle?: boolean;
	deleteProject?: (project: Project) => Promise<void>;
	deleteSelectedFormGroups?: (ids: string[]) => Promise<void>;
	fetchFormGroupsForState?: (ids: string[]) => Promise<void>;
	formGroups: FormGroup[];
	formTemplates?: FormTemplateForResolver[];
	getProject?: (id: string) => Promise<Project | null>;
	getSingleFormGroup?: (data: Pick<FormGroup, "id" | "name"> & Partial<FormGroup>) => Promise<void>;
	handleCreateNewClick?: () => void;
	online: boolean;
	parentProject?: Project | null;
	selected: string[];
	setSelected: React.Dispatch<React.SetStateAction<string[]>>;
	setupDrawer: (location: Location | "") => void;
	submitCreate?: (partialFormGroup: NewFormGroupData) => Promise<FormGroup | undefined>;
	user: UserForResolver;
	updateProject?: (project: Project) => Promise<void>;
	updateProjectsSyncMenu?: (project: Project) => void;
	persisted?: boolean;
}

function FormGroupList(props: Props) {
	const {
		addFormGroupsInState,
		closeCreateNew,
		color,
		createNewToggle,
		deleteProject,
		deleteSelectedFormGroups,
		fetchFormGroupsForState,
		formGroups,
		formTemplates,
		getProject,
		getSingleFormGroup,
		handleCreateNewClick,
		online,
		parentProject,
		selected,
		setSelected,
		setupDrawer,
		submitCreate,
		user,
		updateProject,
		updateProjectsSyncMenu,
		persisted,
	} = props;
	const pageSize = 100;

	const [pageIndex, setPageIndex] = useState(1);
	const [query, setQuery] = useState("");
	const [numSort, setNumSort] = useState({
		ascending: true,
		enabled: true,
	});
	const [filterOption, setFilterOption] = useState<"All" | "Default" | "Photo Logs" | "">("");
	const [devFormGroupOptionsOpen, setDevFormGroupOptionsOpen] = useState(false);
	const params = useParams();

	// Initialize analytics for tracking form group events
	const { trackUserBehaviorEvent } = useAnalytics({
		userId: user?.id || "",
		groupId: user?.group || "",
		online,
		autoStart: false,
	});

	const navigate = useNavigate();
	const location = useLocation();
	const syncMenu = location.pathname.includes("sync-menu");
	const [project, setProject] = useState<Project | null>(null);
	const { openDropdownId, setOpenDropdownId } = useClickOutside(dropdownData, user);
	const [openToast, setOpenToast] = useState(false);
	const [offlineFormGroups, setOfflineFormGroups] = useState<FormGroup[]>([]);
	const [toastContent, setToastContent] = useState<ToastContent>({
		title: "",
		message: "",
		type: "success",
	});
	const { showBoundary } = useErrorBoundary();
	const { projectConfigs } = useProjectConfig(user);
	const { addTemplatesToAutoSync } = useAutoSync(projectConfigs);
	const [forceUpdate, setForceUpdate] = useState(false);
	const [uniqueIds, setUniqueIds] = useState<number>(0);
	const [descExpanded, setDescExpanded] = useState(false);
	const [descOverflowing, setDescOverflowing] = useState(false);
	const descriptionRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setDescExpanded(false);
		setDescOverflowing(false);
		const el = descriptionRef.current;
		if (!el) return;

		const checkOverflow = () => {
			setDescOverflowing(el.scrollHeight > el.clientHeight);
		};
		const observer = new ResizeObserver(checkOverflow);
		observer.observe(el);
		checkOverflow();

		return () => observer.disconnect();
	}, [project?.id]);

	// When the parent (useProjects) resolves syncFormTemplates in the background, sync it into
	// local project state so the template auto-sync effect fires correctly.
	useEffect(() => {
		if (!parentProject?.syncFormTemplates) return;
		setProject((prev) => {
			if (!prev || prev.id !== parentProject.id) return prev;
			if (prev.syncFormTemplates === parentProject.syncFormTemplates) return prev;
			return { ...prev, syncFormTemplates: parentProject.syncFormTemplates };
		});
	}, [parentProject?.syncFormTemplates, parentProject?.id]);

	const { progress, setProgress } = useProgress();

	// CSV Upload state
	const [csvPreview, setCsvPreview] = useState<ChangePreview | null>(null);
	const [csvUploading, setCsvUploading] = useState(false);

	useEffect(() => {
		async function getProjectAgain(id: string | undefined) {
			if (!id) {
				setProject(null);
				return;
			} else if (getProject) {
				let retrieved = await getProject(id);
				if (retrieved?.sync) {
					setProject(retrieved);
				} else {
					if (online) {
						showBoundary(
							new Error(
								"Project is not found Online, but online mode is enabled. To access the project, please ensure it is synced online or switch to offline mode.",
							),
						);
					} else {
						showBoundary(
							new Error(
								"Project is not found offline, and you are offline. To access the project, please ensure it is synced offline or switch to online mode.",
							),
						);
					}
					setProject(null);
				}
			}
			const offlineFormGroups = await db.formGroup.where("projectId").equals(id).toArray();
			setOfflineFormGroups(offlineFormGroups);
		}
		getProjectAgain(params.projectId);
	}, [params.projectId]);

	useEffect(() => {
		const syncProjectOnlyTemplates = (autoSyncFormTemplates: string[]): FormTemplate[] => {
			if (!formTemplates || !project) return [];
			const result = [];
			// N^2 situation here, but dealing with templates so well be working with small lists within groups for now...
			for (let i = 0; i < formTemplates.length; i++) {
				const template = formTemplates[i];
				if (template.projectId && template.projectId === project.id) {
					result.push(template);
					continue;
				}
				// need to check the autoSyncFormTemplates to see if this template is in there via id
				const found = autoSyncFormTemplates.find((id) => id === template.id);
				if (found) {
					result.push(template);
				}
			}
			return result;
		};
		const syncSelectedToDevice = async () => {
			if (!formTemplates || !project) return;
			const autoSyncFormTemplates = project?.syncFormTemplates;
			if (autoSyncFormTemplates && autoSyncFormTemplates.length > 0) {
				const templatesToAutoSync = syncProjectOnlyTemplates(autoSyncFormTemplates);

				await syncSelectedTemplatesOffline(templatesToAutoSync);
				await addTemplatesToAutoSync(templatesToAutoSync);
			}
		};
		if (formTemplates) {
			syncSelectedToDevice();
		}
		setPageIndex(1);
	}, [params.projectId, project?.id, project?.syncFormTemplates?.length, formTemplates]);

	useEffect(() => {
		const uniqueIds = new Set<string>();
		formGroups.forEach((group) => uniqueIds.add(group.id));
		offlineFormGroups.forEach((group) => uniqueIds.add(group.id));
		setUniqueIds(uniqueIds.size);
	}, [formGroups, offlineFormGroups]);

	const filteredFormGroups = useMemo(() => {
		let combined = [...formGroups];
		if (project) {
			for (const offlineFormGroup of offlineFormGroups) {
				if (combined.findIndex((fg) => fg.id === offlineFormGroup.id) === -1) {
					combined.push(offlineFormGroup);
				}
			}
		}
		if (filterOption === "Photo Logs") {
			combined = combined.filter((item) => item.type === "photolog");
		} else if (filterOption === "Default") {
			combined = combined.filter((item) => item.type === "default");
		} else if (filterOption === "All") {
			combined = combined.filter((item) => item.type === "default" || item.type === "photolog");
		}
		return combined.filter((item) => item.name.toLowerCase().includes(query.toLowerCase()));
	}, [formGroups, offlineFormGroups, project, filterOption, query]);

	function handleFilterOption(option: "All" | "Default" | "Photo Logs" | "") {
		setFilterOption(option);
	}

	// Moved this entirely into a useMemo because the "renderFormGroupList" function would re-execute every time anything in this component would cause a rerender. This is because in
	// the return (render) statement, we were just calling this function in there. Somebody clicking on the "options," "sort by," and "filter by" buttons/options would cause the
	// list of formGroups to rerender.
	// -Eric
	const finalList = useMemo(() => {
		/**
		 * function to set a form group as selected (the checkbox is clicked)
		 * @param selectedFormGroup document of the selected form group
		 */
		function handleSelect(selectedFormGroup: FormGroup) {
			const selectedCopy = [...selected];
			const indexOf = selectedCopy.indexOf(selectedFormGroup.id);
			if (indexOf < 0) {
				// it doesnt exist in selected yet
				selectedCopy.push(selectedFormGroup.id);
			} else {
				// it exists so needs to be removed from selected
				selectedCopy.splice(indexOf, 1);
			}
			setSelected(selectedCopy);
		}

		async function handleOpen(formGroup: FormGroup) {
			const newPath = `/project/${formGroup.projectId}/tabs/${formGroup.id}`;
			if (getSingleFormGroup && user) {
				await getSingleFormGroup(formGroup);

				// Track form group edit event
				trackUserBehaviorEvent("form_group_edit", {
					formGroupId: formGroup.id,
					formGroupName: formGroup.name,
					online: navigator.onLine,
				});

				navigate(newPath);
			}
		}

		/**
		 * Builds html for a form group list item
		 * @param formGroup the form group
		 * @returns the html for the list item
		 */
		function renderFormGroupListItem(formGroup: FormGroup, i: number) {
			const html = (
				// <React.Fragment key={i}>
				<FormGroupListItem
					addFormGroupsInState={addFormGroupsInState}
					color={color}
					key={formGroup.id}
					handleOpen={handleOpen}
					selected={selected}
					handleSelect={handleSelect}
					formGroup={formGroup}
					user={user}
					online={online}
					setOpenToast={setOpenToast}
					forceUpdate={forceUpdate}
					setToastContent={setToastContent}
				/>
				// </React.Fragment>
			);
			return html;
		}

		function renderFormGroupList() {
			let formGroupsCopy = [...formGroups];
			if (project) {
				if (offlineFormGroups.length > 0) {
					for (let i = 0; i < offlineFormGroups.length; i++) {
						const offlineFormGroup = offlineFormGroups[i];
						const foundIndex = formGroupsCopy.findIndex((fg) => fg.id === offlineFormGroup.id);
						if (foundIndex === -1) {
							offlineFormGroup.offline = true;
							formGroupsCopy.push(offlineFormGroup);
						} else {
							formGroupsCopy[foundIndex].offlineAndOnline = true;
						}
					}
				}
			}

			if (numSort.enabled && numSort.ascending) {
				formGroupsCopy = sortByAlphaNumericalArray({ array: formGroupsCopy, key: "name" });
			} else if (numSort.enabled && !numSort.ascending) {
				formGroupsCopy = sortByAlphaNumericalArray({ array: formGroupsCopy, key: "name", descending: true });
			} else {
				formGroupsCopy = sortByDate({ array: formGroupsCopy, key: "createdAt" });
			}
			if (filterOption === "Photo Logs") {
				formGroupsCopy = formGroupsCopy.filter((item) => item.type === "photolog");
			} else if (filterOption === "Default") {
				formGroupsCopy = formGroupsCopy.filter((item) => item.type === "default");
			} else if (filterOption === "All") {
				formGroupsCopy = formGroupsCopy.filter((item) => item.type === "default" || item.type === "photolog");
			}

			//this is the search bar functionality
			formGroupsCopy = formGroupsCopy.filter((item) => item.name.toLowerCase().includes(query.toLowerCase()));
			return paginate({ items: formGroupsCopy, pageIndex, pageSize }).map((formGroup, index) => renderFormGroupListItem(formGroup, index));
		}
		return renderFormGroupList();
	}, [
		color,
		filterOption,
		formGroups,
		getSingleFormGroup,
		numSort.ascending,
		numSort.enabled,
		pageIndex,
		query,
		selected,
		setSelected,
		user,
		addFormGroupsInState,
		project?.forceUpdateData,
		forceUpdate,
	]);

	function handleSelectAll(e: React.ChangeEvent<HTMLInputElement>) {
		if (e.target.checked) {
			setSelected(filteredFormGroups.map((group) => group.id));
		} else {
			setSelected([]);
		}
	}

	async function handleGISCsvUpload(file: File) {
		if (!project || !formTemplates) {
			return;
		}

		setCsvUploading(true);
		setCsvPreview(null);

		try {
			const csvText = await file.text();
			// const formGroupIds = formGroups.map((fg) => fg.id);
			const formGroupIds = selected;
			// const fieldTemplates = await batchGetFieldTemplatesFromFormGroup(formGroups, online);
			// const fieldInstances = await Promise.all(formGroups.map((fg) => batchGetFieldInstancesByFormGroup(fg.id, online))).then((arrays) => arrays.flat());
			const syncConfig: SyncConfig = {
				online,
				source: "remote",
				destination: "json",
			};

			const json = await downloadFormGroupJson(project!, formGroupIds, syncConfig);
			const stringifiedJson = JSON.stringify(json);

			// Store a backup of the selected form groups, in case something unexpected happens.
			// Create download anchor + download
			const date = new Date();
			const year = date.getFullYear().toString().slice(-2); // Last 2 digits of the year
			const month = (date.getMonth() + 1).toString().padStart(2, "0"); // The month with a leading zero if less than 2 characters
			const day = date.getDate().toString().padStart(2, "0"); // The day with a leading 0 if it's less than 2 characters
			const hour = date.getHours().toString().padStart(2, "0"); // The hour with a leading 0 if less than 2 characters
			const minute = date.getMinutes().toString().padStart(2, "0"); // The minutes with a leading 0 if less than 2 characters
			const seconds = date.getSeconds().toString().padStart(2, "0"); // The seconds with a leading 0 if less than 2 characters
			const finalTime = `${hour}${minute}${seconds} ${month}-${day}-${year}`;
			const url = `${user.group}/gis-csv-backups/${project.name}/${user?.username || "UnknownUser"}-${finalTime}.json`;
			try {
				await uploadData({
					key: url,
					data: stringifiedJson,
				}).result;
			} catch (error) {
				const message = "ERROR SAVING GIS JSON TO S3 BUCKET";
				recordSentry({ error, msg: message });
			}

			const { formGroups: retrievedFormGroups, fieldTemplates, fieldInstances } = json;

			const preview = await previewGeoCSVImport(project?.id!, csvText, retrievedFormGroups, fieldTemplates, fieldInstances);

			setCsvPreview(preview);
		} catch (error) {
			console.error("CSV upload error:", error);

			// for errors we need a special preview call to set it as the csvPreview and
			// be handled in the confirmation part of the process
			// const errorPreview: ChangePreview = {
			// 	success: false,
			// 	warnings: [error instanceof Error ? error.message : "An unknown error occurred during CSV upload"],

			// };
			// setCsvPreview(errorPreview);
		} finally {
			setCsvUploading(false);
		}
	}

	async function handleApplyCsvImport() {
		if (!csvPreview) return;

		try {
			setCsvUploading(true);
			const result = await applyGeoCSVImportFromPreview(csvPreview);
			if (result.success) {
				setToastContent({
					title: "CSV Import Applied",
					message: `Successfully applied ${result.appliedUpdates} updates`,
					type: "success",
				});
				setCsvPreview(null); // Clear preview after successful import
			} else {
				setToastContent({
					title: "CSV Import Failed",
					message: result.errors?.join(", ") || "Unknown error occurred",
					type: "destructive",
				});
			}
			setOpenToast(true);
		} catch (error) {
			console.error("Apply import error:", error);
			setToastContent({
				title: "CSV Import Error",
				message: error instanceof Error ? error.message : "Failed to apply CSV import",
				type: "destructive",
			});
			setOpenToast(true);
		} finally {
			setCsvUploading(false);
		}
	}

	return project || syncMenu ? (
		<>
			<ConfirmationModal
				colorPalette={color}
				details={toastContent.message}
				confirmTitle={toastContent.title}
				open={openToast}
				setOpen={setOpenToast}
				toast={toastContent.type}
				timeout={5000}
				canClose
			/>
			{user?.isDeveloper && devFormGroupOptionsOpen ? (
				<DevFormGroupOptions formGroups={formGroups} project={project} setDevFormGroupOptionsOpen={setDevFormGroupOptionsOpen} />
			) : (
				<div
					data-test={`form-group-list-project-name: ${project?.name}`}
					id={project?.id}
					className={syncMenu ? styles.syncFormGroup_list_content_wrapper : styles.formGroup_list_content_wrapper}>
					{createNewToggle && getSingleFormGroup && (
						<CreateFormGroupFolder
							closeCreateNew={closeCreateNew}
							formGroups={formGroups}
							formTemplates={formTemplates}
							pageSize={pageSize}
							project={project}
							setPageIndex={setPageIndex}
							submitCreate={submitCreate}
							user={user}
							getSingleFormGroup={getSingleFormGroup}
							online={online}
						/>
					)}
					{progress.show && (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: 16,
								padding: 24,
								background:
									progress.variant === "info"
										? color.background.primary
										: color.toastBackground[progress.variant as keyof Colors["toastBackground"]],
							}}
							data-test="sync-progress"
							data-sync-variant={progress.variant}>
							<ProgressDisplay
								colors={color}
								progress={progress}
								setProgress={setProgress}
								action={
									progress.downloadUrl ? (
										<a
											href={progress.downloadUrl}
											download
											onClick={(e) => e.stopPropagation()}
											style={{
												marginTop: 16,
												marginBottom: 4,
												padding: "6px 12px",
												fontWeight: 700,
												textDecoration: "none",
												background: color.buttonBackground.outlined,
												color: color.buttonText.outlined,
												borderRadius: 4,
												border: `1px solid ${color.buttonBorder.outlined}`,
											}}>
											{progress.downloadLabel ?? "Download"}
										</a>
									) : undefined
								}
							/>
						</div>
					)}

					{/* CSV Upload Processing Indicator */}
					{csvUploading && (
						<div
							style={{
								padding: "10px",
								textAlign: "center",
								backgroundColor: color.background.secondary,
								color: color.text.primary,
								borderRadius: "4px",
								margin: "10px 0",
							}}>
							Processing CSV file...
						</div>
					)}
					{!syncMenu && project && (
						<div className={styles.projectDetails} style={progress.show ? { paddingTop: "8px" } : undefined}>
							<div style={{ color: color.text.primary }} className={styles.projectName} data-test="project-name">
								{project.name}
							</div>

							<div
								ref={descriptionRef}
								style={{ color: color.text.primary }}
								className={`${styles.projectDescription} ${!descExpanded ? styles.projectDescriptionClamped : ""}`}
								data-test="project-description">
								{project.description}
							</div>
							{(descOverflowing || descExpanded) && (
								<span
									className={styles.descViewToggle}
									style={{ color: color.text.secondary }}
									onClick={() => setDescExpanded((prev) => !prev)}>
									{descExpanded ? "View less" : "View more"}
								</span>
							)}
						</div>
					)}
					<div id="formgroupListTools" className={syncMenu ? styles.syncList_tools : styles.groupList_tools}>
						{syncMenu
							? null
							: deleteProject &&
								deleteSelectedFormGroups &&
								updateProjectsSyncMenu &&
								addFormGroupsInState &&
								getProject &&
								handleCreateNewClick &&
								project &&
								fetchFormGroupsForState && (
									<FormGroupListOptions
										setupDrawer={setupDrawer}
										color={color}
										deleteProject={deleteProject}
										deleteSelectedFormGroups={deleteSelectedFormGroups}
										formGroups={formGroups}
										getProject={getProject}
										handleCreateNewClick={handleCreateNewClick}
										handleFilterOption={handleFilterOption}
										handleGISCsvUpload={handleGISCsvUpload}
										handleApplyCsvImport={handleApplyCsvImport}
										csvPreview={csvPreview}
										filterOption={filterOption}
										online={online}
										openDropdownId={openDropdownId}
										project={project}
										query={query}
										selected={selected}
										setDevFormGroupOptionsOpen={setDevFormGroupOptionsOpen}
										setNumSort={setNumSort}
										numSort={numSort}
										setOpenDropdownId={setOpenDropdownId}
										setQuery={setQuery}
										setSelected={setSelected}
										user={user}
										pageSize={pageSize}
										pageIndex={pageIndex}
										setPageIndex={setPageIndex}
										fetchFormGroupsForState={fetchFormGroupsForState}
										formTemplates={formTemplates}
										updateProject={updateProject}
										updateProjectsSyncMenu={updateProjectsSyncMenu}
										addFormGroupsInState={addFormGroupsInState}
										setForceUpdate={setForceUpdate}
										persisted={persisted}
										setProgress={setProgress}
									/>
								)}
						<div className={styles.pagination_nation}>
							<Pagination
								currentPage={pageIndex}
								totalPages={Math.ceil(formGroups.length / pageSize)}
								setCurrentPage={setPageIndex}
								colorPalette={color}
							/>
						</div>
					</div>
					<div className={location.pathname.includes("sync-menu") ? styles.syncList_wrapper : styles.formGroupList_wrapper}>
						{syncMenu && (
							<div className={styles.sync_pagination_nation}>
								<Pagination
									currentPage={pageIndex}
									totalPages={Math.ceil(formGroups.length / pageSize)}
									setCurrentPage={setPageIndex}
									colorPalette={color}
								/>
							</div>
						)}
						<div
							style={{
								backgroundColor: syncMenu ? color.background.secondary : color.background.tertiary,
								color: color.text.primary,
							}}
							className={syncMenu ? styles.syncHead_wrapper : styles.form_headWrapper}>
							<div className={styles.checkboxHead}>
								<input
									name="select-all-checkbox"
									id="select-all-checkbox"
									className={styles.selectAll}
									type="checkbox"
									onChange={handleSelectAll}
									checked={selected.length === filteredFormGroups.length && selected.length > 0}
									style={{
										background: color.background.primary,
									}}
								/>
							</div>
							<div className={styles.nameHead}>Folder(s)</div>
						</div>
						<div data-test="form-group-list" className={syncMenu ? styles.syncFormGroup_wrapper : styles.formGroup_wrapper}>
							{finalList}
						</div>
					</div>
				</div>
			)}
			;
		</>
	) : (
		<LoadingSpinner />
	);
}

export default FormGroupList;
